import type { Screen } from '../types'

/**
 * Handles — the two left-rail destinations of design brief §6.18.
 *
 * # These specs carry no invented data, and that is the whole of them
 *
 * Every other file in this directory is the KOVA prototype transcribed
 * verbatim: fixture rows an operator can look at before anything is wired. There
 * is nothing to transcribe here — the prototype predates §6.18 — and inventing
 * fixtures for these two screens would be worse than an empty spec in a way
 * specific to what they are:
 *
 *   - A fabricated reserved list is a list of words this deployment does NOT
 *     keep back, drawn on the screen an operator reads to answer "why can I not
 *     have that name". The real list ships seeded with fifteen rows, so the
 *     fixture would look entirely plausible and be wrong about every one.
 *   - A fabricated review queue is worse still: an invented held handle is an
 *     invented person waiting, and an invented empty queue says nobody is.
 *
 * Both screens are wired to `/v1/admin` (see `SCREEN_LOADERS`), so a live read
 * always replaces these blocks and they are never drawn in a running console.
 * They exist because the IA test requires a spec for every nav entry, and each
 * one says what the screen is instead of pretending to be it.
 *
 * # Neither screen declares `roles`, deliberately
 *
 * The console's role gate compares against the DESIGN's seven labels and treats
 * `Admin` as universal, which is true of how the corpus labels screens and false
 * of the backend's grants: `admin` is absent from `staff.implies`, so an Admin
 * session that was never granted compliance does not hold
 * `handle.impersonation.decide`. A `roles: ['Compliance', 'Admin']` here would
 * produce the sentence "requires Compliance or Admin" beside a control the
 * server refuses an Admin — a second, wrong, permission story competing with the
 * right one. The decision control is gated on the permission closure the backend
 * reports and on nothing else; the shell's own gate still applies for a standby
 * cell, which no permission changes.
 *
 * # The law numbers here are the CORPUS's, not CLAUDE.md's
 *
 * `laws.ts` carries nineteen laws transcribed from the prototype; CLAUDE.md Part
 * II carries twenty-one in a different order. They are not the same numbering
 * and this field indexes the first: corpus 18 is "cell aware — standby disables
 * mutating controls" and corpus 19 is "stale not down", which are the two these
 * screens most need on the header. CLAUDE.md's law 18 (a gated control is shown
 * disabled with the role named, never hidden) and law 21 (a mutation states what
 * will be audited before it runs) both govern the decision control and are
 * honoured in the block itself — they simply have no chip to cite, because the
 * corpus's list predates them.
 */
export const HANDLES_SCREENS: Readonly<Record<string, Screen>> = {
  'hd-reviews': {
    title: 'Impersonation review queue',
    purpose:
      'The queue a human works. A claim whose confusable fold matched a reserved term published with action: review is HELD — the name is taken against everybody else and resolves to nobody while it waits, so the creator sitting here has a pay-me link that does not work yet. Select a row to read the claim that produced the hold and to allow or refuse the name.',
    laws: [5, 18, 19],
    subject: 'handle_impersonation_review',
    empty: [
      'No handles are held for review',
      'Nobody is waiting on a person. A claim is held here only when its confusable fold matches a reserved term published with action: review — today that is "official", the one seeded term of fifteen that carries it. A near-empty queue is what this control is expected to look like, not a screen that failed to load.',
    ],
    blocks: [
      {
        t: 'note',
        tone: 'n',
        kicker: 'Design corpus',
        title: 'This screen has no fixture rows, on purpose',
        body: 'It is wired to GET /v1/admin/handles/reviews, so what you see in a running console is always a live read. There is no invented queue behind this spec because an invented held handle is an invented person waiting with a dark pay-me link, and an invented empty queue is the sentence "nobody is held" said by a screen that read nothing.',
        ref: 'design brief §6.18.1 · go-wallet-admin-client#111',
      },
    ],
  },

  'hd-terms': {
    title: 'Reserved terms',
    purpose:
      'The published list of names nobody may claim, and the confusable fold each term is actually matched on. Publishing "support" also refuses "supp0rt", "s-u-p-p-o-r-t" and "5upport", so the fold is a primary column rather than an implementation detail — an operator who cannot see it cannot predict what has been reserved. Read only: publishing is Phase 2 and cannot be made safe until a fold-preview route exists.',
    laws: [5, 6, 18],
    subject: 'handle_reserved_term',
    empty: [
      'The reserved list is empty, and that is an alarm',
      'This table ships seeded with fifteen rows, so an empty list is not the ordinary case — it means no word is kept back and every operator name is claimable right now, @support and @billing and @security among them. Check whether the seed ran rather than treating this as a list nobody has filled in.',
    ],
    blocks: [
      {
        t: 'note',
        tone: 'n',
        kicker: 'Design corpus',
        title: 'This screen has no fixture rows, on purpose',
        body: 'It is wired to GET /v1/admin/handles/reserved-terms, so what you see in a running console is always a live read. A fabricated reserved list would be a list of words this deployment does not actually keep back, drawn on the one screen an operator reads to answer why a customer cannot have a name — plausible in every row and wrong in all of them.',
        ref: 'design brief §6.18.2 · go-wallet-admin-client#111',
      },
    ],
  },
}
